import { Callout, Separator } from "@/components/ui";
import { useGetPatient } from "@/features/Patient/network/usePatient";
import { useGetGenericObservations } from "@/network/useGenericObservations";
import { useParams } from "@tanstack/react-router";
import { CircleUserIcon, Cross } from "lucide-react";
import { GenericObservationEditor } from "../components/GenericObservationEditor";
import { SectionContent } from "../components/SectionContent";
import { SectionHeader, SectionSubheader } from "../components/SectionHeader";
import { AddressEditor } from "./components/AddressEditor";
import { EmergencyContactEditor } from "./components/EmergencyContactEditor";
import { GenderEditor } from "./components/GenderEditor";
import { PharmacyEditor } from "./components/PharmacyEditor";
import { PhoneNumberEditor } from "./components/PhoneNumberEditor";
import { PreferredNameEditor } from "./components/PreferredNameEditor";
import { ProfileSkeleton } from "./components/ProfileSkeleton";
import { PronounsEditor } from "./components/PronounsEditor";
import { useGetProfile } from "./network/useProfile";

export const Profile = () => {
  const { patientId } = useParams({ strict: false });

  const {
    data: profile,
    isLoading: isLoadingProfile,
    isError: isErrorProfile,
    refetch: refetchProfile,
  } = useGetProfile(patientId);

  const {
    data: profileGroupItems,
    isLoading: isLoadingProfileGroupItems,
    isError: isErrorProfileGroupItems,
    refetch: refetchProfileGroupItems,
  } = useGetGenericObservations(patientId, "profile");

  const { data: patient } = useGetPatient(patientId);

  const isLoading = isLoadingProfile || isLoadingProfileGroupItems;
  const isError =
    !isLoading && (isErrorProfile || isErrorProfileGroupItems || !profile);
  const isSuccess = !isLoading && !isError && profile;
  const refetch = () => {
    refetchProfile();
    refetchProfileGroupItems();
  };

  return (
    <div className="w-full h-full flex flex-col">
      <SectionHeader title="Profile" showSeparator={false} />
      <SectionContent>
        <SectionSubheader title="Patient" iconComponent={CircleUserIcon} />
        {isLoading && <ProfileSkeleton />}
        {isError && (
          <div className="flex flex-col gap-2 py-2 w-full">
            <Callout
              className="w-full cursor-pointer"
              variant="danger"
              title="Unable to load profile!"
              description="Please click here to try again."
              onClick={() => refetch()}
            />
          </div>
        )}
        {isSuccess && (
          <>
            <PhoneNumberEditor
              patientId={patientId as string}
              profile={profile}
            />
            <Separator />
            <AddressEditor patientId={patientId as string} profile={profile} />
            <Separator />
            {patient && (
              <>
                <PreferredNameEditor patient={patient} />
                <Separator />
                <div className="flex flex-row">
                  <div className="flex flex-col flex-1 w-full">
                    <GenderEditor patient={patient} />
                  </div>
                  <div className="flex flex-col flex-1 w-full">
                    <PronounsEditor patient={patient} />
                  </div>
                </div>
                <Separator />
              </>
            )}
            <div className="flex flex-row">
              {profileGroupItems?.items?.map((item) => (
                <GenericObservationEditor
                  key={item.observationCode}
                  item={item}
                  itemCategory="social-history"
                  group="profile"
                />
              ))}
            </div>
            <Separator />

            <PharmacyEditor patientId={patientId as string} />
          </>
        )}
        <SectionSubheader title="Emergency Contact" iconComponent={Cross} />
        <EmergencyContactEditor patientId={patientId as string} />
      </SectionContent>
    </div>
  );
};
